import React from 'react';
import Highcharts from 'highcharts';
import styled from 'styled-components';
import times from 'lodash/times';
import random from 'lodash/random';
import { Chart } from '../Chart';
import { chartSettings } from './ColumnChart';
import { Box } from '../../Box/Box';
import { pulse, Shimmer } from '../../Shimmer/Shimmer';
import { Wrapper } from '../../Wrapper/Wrapper';
import { MarginProps } from '../../../../theme/styledProps';
import { theme } from '../../../../theme/theme';

const COLORS_COUNT = 4;
const MAX_VALUE = 4;
const MIN_VALUE = 1;

const loadingChartSettings: Highcharts.Options = {
  ...chartSettings,

  legend: {
    enabled: false,
  },

  tooltip: {
    enabled: false,
  },

  plotOptions: {
    series: {
      marker: {
        enabled: false,
      },

      states: {
        inactive: {
          opacity: 1,
        },

        hover: {
          enabled: false,
        },
      },
    },
  },
};

export const StyledChart = styled(Wrapper)`
  position: relative;
  height: calc(100% - 48px);

  .highcharts-series rect {
    animation: ${pulse} 1s infinite alternate;
  }
`;

interface ColumnChartLoadingProps extends MarginProps {
  seriesCount?: number;
  categoriesCount?: number;
  isStacked?: boolean;
  chartHeight?: number;
}

export const ColumnChartLoading: React.FC<ColumnChartLoadingProps> = React.memo(
  ({ seriesCount = 4, categoriesCount = 4, chartHeight, isStacked }) => {
    const options: Highcharts.Options = {
      ...loadingChartSettings,
      chart: {
        ...loadingChartSettings.chart,
        height: chartHeight,
      },

      plotOptions: {
        ...loadingChartSettings.plotOptions,

        column: {
          stacking: isStacked ? 'normal' : undefined,
        },
      },

      series: times(seriesCount).map(i => ({
        type: 'column',
        name: i.toString(),
        data: times(categoriesCount).map(() => random(MIN_VALUE, MAX_VALUE)),
      })),

      colors: times(categoriesCount).map(i => theme.colors.neutral[((i % COLORS_COUNT) + 1) * 100]),

      xAxis: {
        categories: times(categoriesCount, String),
      },
    };

    return (
      <StyledChart>
        <Chart options={options} />

        <Box
          height={24}
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt="24"
        >
          <Shimmer height={24} width={120} mr="8" />
          <Shimmer height={24} width={120} ml="8" />
        </Box>
      </StyledChart>
    );
  },
);
